import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import API from 'services/api';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/styles';

const PAYMENT_METHODS = {
  BANK: 'bank',
  OP_INVOICE: 'op_invoice',
};

const PROVIDER_ERROR =
  'Maksutapoja ei voitu ladata. Yritä hetken kuluttua uudelleen.';
const PAYMENT_ERROR =
  'Maksutapaa ei voitu avata. Yritä uudelleen tai valitse toinen maksutapa.';
const RESUME_ERROR = 'Maksua ei voitu jatkaa. Yritä hetken kuluttua uudelleen.';
const PROVIDER_SELECTION_ERROR =
  'Valittua maksutapaa ei voitu avata. Valitse toinen maksutapa.';

const euroFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
    // fullScreen pins the paper to the viewport height, which leaves the
    // scroll="body" container nothing to scroll. The paper has to stay its own
    // scroller there, so clipping it would strand the providers below the fold.
    '&.MuiDialog-paperFullScreen': {
      overflowY: 'auto',
    },
  },
  paymentHeader: {
    padding: theme.spacing(4, 6, 1),
    paddingRight: theme.spacing(9),
    fontFamily: 'futura-pt, sans-serif',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '2.25rem',
    lineHeight: 1.25,
    color: '#159D89',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 3, 1),
      paddingRight: theme.spacing(8),
      fontSize: '1.75rem',
    },
  },
  paymentContent: {
    padding: theme.spacing(2, 6, 5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 3, 4),
    },
  },
  priceSummary: {
    padding: theme.spacing(2.25, 2.5),
    border: '1px solid #DDE9E6',
    borderRadius: theme.spacing(1.5),
    background: '#F7FAF9',
  },
  paymentInfo: {
    margin: 0,
    fontFamily: 'futura-pt, sans-serif',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '1.125rem',
    lineHeight: 1.45,
    color: '#666D6B',
  },
  totalPrice: {
    marginTop: theme.spacing(0.75),
    fontWeight: 600,
    color: '#343A38',
  },
  errorAlert: {
    marginTop: theme.spacing(2),
  },
  terms: {
    marginTop: theme.spacing(2),
  },
  termsLink: {
    color: '#126A5D',
    fontWeight: 600,
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
  },
  providerSection: {
    marginTop: theme.spacing(3),
  },
  providerHeading: {
    marginBottom: theme.spacing(1.5),
    fontFamily: 'futura-pt, sans-serif',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#343A38',
  },
  providerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(2),
    background: '#F3F7F6',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
    },
    '@media (max-width: 380px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  providerTile: {
    minWidth: 0,
    minHeight: 128,
    padding: theme.spacing(1.75),
    border: '1px solid #D6E2DF',
    borderRadius: theme.spacing(1.5),
    background: '#FFFFFF',
    color: '#343A38',
    textTransform: 'none',
    boxShadow: '0 2px 7px rgba(40, 76, 69, 0.07)',
    transition: 'border-color 140ms ease, box-shadow 140ms ease',
    '&:not(.Mui-disabled):hover': {
      borderColor: '#20BDA6',
      background: '#FFFFFF',
      boxShadow: '0 5px 14px rgba(32, 189, 166, 0.14)',
    },
    '&.Mui-focusVisible, &:focus-visible': {
      outline: '3px solid #126A5D',
      outlineOffset: '2px',
    },
    '&.Mui-disabled': {
      color: '#68716F',
      background: '#F7F9F8',
      opacity: 0.72,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  selectedProviderTile: {
    borderColor: '#159D89',
    boxShadow: '0 0 0 2px rgba(21, 157, 137, 0.16)',
  },
  providerTileContent: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  providerLogo: {
    display: 'block',
    width: '100%',
    maxWidth: 132,
    height: 42,
    objectFit: 'contain',
  },
  providerName: {
    maxWidth: '100%',
    fontFamily: 'futura-pt, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.25,
    overflowWrap: 'anywhere',
    textAlign: 'center',
  },
  invoiceBadge: {
    display: 'inline-flex',
    maxWidth: '100%',
    padding: theme.spacing(0.5, 0.8),
    border: '1px solid #BCE1DA',
    borderRadius: 999,
    background: '#EAF7F4',
    color: '#126A5D',
    fontFamily: 'futura-pt, sans-serif',
    fontSize: '0.72rem',
    fontWeight: 600,
    lineHeight: 1.25,
    whiteSpace: 'normal',
  },
  paymentStatus: {
    minHeight: 34,
    marginTop: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    fontFamily: 'futura-pt, sans-serif',
    fontSize: '0.95rem',
    color: '#59615F',
  },
  circularProgress: {
    color: '#159D89',
  },
  retryButton: {
    marginTop: theme.spacing(1.5),
    color: '#126A5D',
    borderColor: '#87BEB4',
    fontFamily: 'futura-pt, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
    '&.Mui-focusVisible, &:focus-visible': {
      outline: '3px solid #126A5D',
      outlineOffset: '2px',
    },
  },
  confirmSection: {
    marginTop: theme.spacing(2.5),
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'stretch',
    },
  },
  confirmButton: {
    minWidth: 220,
    padding: theme.spacing(1.5, 4),
    borderRadius: theme.spacing(1),
    background: '#159D89',
    color: '#FFFFFF',
    fontFamily: 'futura-pt, sans-serif',
    fontSize: '1.0625rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:not(.Mui-disabled):hover': {
      background: '#126A5D',
      boxShadow: 'none',
    },
    '&.Mui-focusVisible, &:focus-visible': {
      outline: '3px solid #126A5D',
      outlineOffset: '2px',
    },
    '&.Mui-disabled': {
      background: '#E3EAE8',
      color: '#7A8280',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  closeButton: {
    position: 'absolute',
    width: 44,
    height: 44,
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: '#159D89',
    '&.Mui-focusVisible, &:focus-visible': {
      outline: '3px solid #126A5D',
      outlineOffset: '2px',
    },
  },
  hiddenForm: {
    display: 'none',
  },
}));

const parameterToInput = (param) => (
  <input key={param.name} type="hidden" name={param.name} value={param.value} />
);

const responseToForms = (response, classname, id) =>
  (response.providers || []).map((provider, index) => (
    <form
      className={classname}
      key={provider.id || provider.name}
      method={provider.method || 'POST'}
      action={provider.url}
      id={`form-${id}-${index}`}
    >
      {(provider.parameters || []).map((param) => parameterToInput(param))}
      <input type="submit" id={`submit-${id}-${index}`} />
    </form>
  ));

const getErrorMessage = (error, fallback) =>
  error.userMessage || error.response?.data?.msg || fallback;

const isPaymentQuoteChanged = (error) => {
  const code = error.response?.data?.code;
  return (
    error.response?.status === 409 &&
    (code === 'PAYMENT_QUOTE_CHANGED' || code === 'PAYMENT_FEE_CHANGED')
  );
};

const isValidPaymentQuote = (quote) =>
  Number.isInteger(quote?.subtotalCents) &&
  quote.subtotalCents >= 0 &&
  typeof quote.token === 'string' &&
  quote.token.trim().length > 0;

const getProviderFee = (provider) => {
  const fee = Number(provider.feeAmount);
  return Number.isFinite(fee) ? Math.max(0, fee) : 0;
};

const getCreatePaymentUrl = (paymentId, provider, quoteToken) => {
  const baseUrl = `/api/payments/create/${paymentId}?paymentMethod=${encodeURIComponent(
    provider.paymentMethod
  )}&providerId=${encodeURIComponent(
    provider.id
  )}&quoteToken=${encodeURIComponent(quoteToken)}`;

  return provider.paymentMethod === PAYMENT_METHODS.OP_INVOICE
    ? `${baseUrl}&expectedFeeAmount=${encodeURIComponent(
        getProviderFee(provider)
      )}`
    : baseUrl;
};

export const PaymentDialog = ({
  visible,
  closeDialog,
  payment,
  initialError,
  mobile,
}) => {
  const classes = useStyles();
  const providerRequestId = useRef(0);
  const paymentRequestId = useRef(0);
  const providerButtonRefs = useRef({});
  const lastAttemptedProviderId = useRef();
  const resumeRetryButtonRef = useRef();
  const confirmButtonRef = useRef();
  const isResumePayment = payment.status === 'paying';
  const hasImmutablePaymentTotal =
    Number.isInteger(payment.paymentTotalCents) &&
    payment.paymentTotalCents >= 0;

  const [providers, setProviders] = useState([]);
  const [paymentQuote, setPaymentQuote] = useState();
  const [paymentData, setPaymentData] = useState();
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedResponseIndex, setSelectedResponseIndex] = useState();
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [providerError, setProviderError] = useState();
  const [paymentError, setPaymentError] = useState();

  const loadProviders = useCallback(async () => {
    const requestId = ++providerRequestId.current;
    setIsLoadingProviders(true);
    setProviderError(undefined);

    try {
      const response = await API.get(`/api/payments/providers/${payment._id}`);
      if (requestId !== providerRequestId.current) {
        return;
      }

      const nextProviders = response.data?.providers;
      const nextQuote = response.data?.quote;
      if (!Array.isArray(nextProviders) || !isValidPaymentQuote(nextQuote)) {
        throw new Error('Invalid payment provider response');
      }
      setPaymentQuote(nextQuote);
      setProviders(nextProviders);
    } catch (error) {
      if (requestId === providerRequestId.current) {
        setProviders([]);
        setPaymentQuote(undefined);
        setProviderError(getErrorMessage(error, PROVIDER_ERROR));
      }
    } finally {
      if (requestId === providerRequestId.current) {
        setIsLoadingProviders(false);
      }
    }
  }, [payment._id]);

  const clearPaymentAttempt = useCallback(() => {
    setPaymentData(undefined);
    setSelectedResponseIndex(undefined);
    setIsCreatingPayment(false);
  }, []);

  const resetPaymentAttempt = useCallback(() => {
    clearPaymentAttempt();
    setSelectedProvider(undefined);
  }, [clearPaymentAttempt]);

  const resumePayment = useCallback(async () => {
    const requestId = ++paymentRequestId.current;
    setIsCreatingPayment(true);
    setPaymentData(undefined);
    setSelectedResponseIndex(undefined);
    setPaymentError(undefined);

    try {
      const response = await API.get(`/api/payments/resume/${payment._id}`);
      if (requestId !== paymentRequestId.current) {
        return;
      }

      const resumedProvider = response.data?.provider;
      if (
        !resumedProvider ||
        typeof resumedProvider !== 'object' ||
        typeof resumedProvider.url !== 'string' ||
        resumedProvider.url.length === 0
      ) {
        throw new Error('Invalid resumed payment response');
      }

      setPaymentData({ providers: [resumedProvider] });
      setSelectedResponseIndex(0);
    } catch (error) {
      if (requestId === paymentRequestId.current) {
        setPaymentError(getErrorMessage(error, RESUME_ERROR));
        resetPaymentAttempt();
      }
    }
  }, [payment._id, resetPaymentAttempt]);

  const createPayment = async (provider) => {
    const requestId = ++paymentRequestId.current;
    lastAttemptedProviderId.current = provider.id;
    setIsCreatingPayment(true);
    setPaymentData(undefined);
    setSelectedResponseIndex(undefined);
    setSelectedProvider(provider);
    setPaymentError(undefined);

    try {
      const response = await API.post(
        getCreatePaymentUrl(payment._id, provider, paymentQuote.token)
      );
      if (requestId !== paymentRequestId.current) {
        return;
      }

      const responseProviders = response.data?.providers || [];
      let providerIndex = responseProviders.findIndex(
        (responseProvider) => responseProvider.id === provider.id
      );

      if (providerIndex === -1 && responseProviders.length === 1) {
        providerIndex = 0;
      }

      if (providerIndex === -1) {
        const error = new Error('Selected payment provider was not returned');
        error.userMessage = PROVIDER_SELECTION_ERROR;
        throw error;
      }

      setPaymentData(response.data);
      setSelectedResponseIndex(providerIndex);
    } catch (error) {
      if (requestId === paymentRequestId.current) {
        setPaymentError(getErrorMessage(error, PAYMENT_ERROR));
        if (isPaymentQuoteChanged(error)) {
          setProviders([]);
          setPaymentQuote(undefined);
          const reloadProviders = loadProviders();
          resetPaymentAttempt();
          await reloadProviders;
          return;
        }
        clearPaymentAttempt();
      }
    }
  };

  const selectProvider = (provider) => {
    setSelectedProvider(provider);
    setPaymentError(undefined);
  };

  const handleDialogClose = () => {
    providerRequestId.current += 1;
    paymentRequestId.current += 1;
    setIsLoadingProviders(false);
    setPaymentQuote(undefined);
    resetPaymentAttempt();
    closeDialog();
  };

  useEffect(() => {
    if (!visible) {
      providerRequestId.current += 1;
      paymentRequestId.current += 1;
      return;
    }

    paymentRequestId.current += 1;
    lastAttemptedProviderId.current = undefined;
    setProviders([]);
    setPaymentQuote(undefined);
    setPaymentData(undefined);
    setSelectedProvider(undefined);
    setSelectedResponseIndex(undefined);
    setIsCreatingPayment(false);
    setPaymentError(initialError);
    if (isResumePayment) {
      resumePayment();
    } else {
      loadProviders();
    }
  }, [initialError, isResumePayment, loadProviders, resumePayment, visible]);

  useEffect(() => {
    if (!visible || !paymentData || selectedResponseIndex === undefined) {
      return undefined;
    }

    let active = true;
    const requestId = paymentRequestId.current;

    const submitProvider = () => {
      try {
        if (!active || requestId !== paymentRequestId.current) {
          return;
        }

        const submitButton = document.getElementById(
          `submit-${payment._id}-${selectedResponseIndex}`
        );
        if (!submitButton) {
          throw new Error('Payment provider form was not rendered');
        }
        submitButton.click();
      } catch (error) {
        if (active && requestId === paymentRequestId.current) {
          setPaymentError(getErrorMessage(error, PAYMENT_ERROR));
          clearPaymentAttempt();
        }
      }
    };

    submitProvider();
    return () => {
      active = false;
    };
  }, [
    payment._id,
    paymentData,
    clearPaymentAttempt,
    selectedResponseIndex,
    visible,
  ]);

  useEffect(() => {
    if (!isCreatingPayment && !isLoadingProviders && paymentError) {
      if (isResumePayment) {
        if (resumeRetryButtonRef.current) {
          resumeRetryButtonRef.current.focus();
        }
      } else if (selectedProvider && confirmButtonRef.current) {
        confirmButtonRef.current.focus();
      } else if (lastAttemptedProviderId.current) {
        const attemptedProviderButton =
          providerButtonRefs.current[lastAttemptedProviderId.current];
        if (attemptedProviderButton) {
          attemptedProviderButton.focus();
        }
      }
    }
  }, [
    isCreatingPayment,
    isLoadingProviders,
    isResumePayment,
    paymentError,
    selectedProvider,
  ]);

  const availableProviders =
    !isResumePayment && paymentQuote
      ? providers.filter(
          (provider) =>
            provider.paymentMethod === PAYMENT_METHODS.BANK ||
            provider.paymentMethod === PAYMENT_METHODS.OP_INVOICE
        )
      : [];
  const selectedFeeCents = selectedProvider
    ? Math.round(getProviderFee(selectedProvider) * 100)
    : 0;
  const totalCents = paymentQuote
    ? paymentQuote.subtotalCents + selectedFeeCents
    : undefined;
  const confirmLabel = totalCents === 0 ? 'Vahvista varaus' : 'Jatka maksamaan';
  const statusText = isLoadingProviders
    ? 'Maksutapoja ladataan…'
    : isCreatingPayment
    ? isResumePayment
      ? 'Maksua jatketaan…'
      : 'Maksua avataan…'
    : '';

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper }}
      fullScreen={mobile}
      fullWidth
      scroll="body"
      maxWidth="lg"
      open={visible}
      onClose={handleDialogClose}
      aria-labelledby="payment-dialog-title"
      aria-describedby={
        !isResumePayment || hasImmutablePaymentTotal
          ? 'payment-dialog-description'
          : undefined
      }
    >
      <DialogTitle id="payment-dialog-title" className={classes.paymentHeader}>
        {payment.products[0].name}
      </DialogTitle>
      <DialogContent className={classes.paymentContent}>
        {!isResumePayment && (
          <div id="payment-dialog-description" className={classes.priceSummary}>
            <Typography className={classes.paymentInfo}>
              Tuotteen hinta: {payment.products[0].price}€
            </Typography>
            {Number(payment.products[0]?.serviceFee) > 0 && (
              <Typography className={classes.paymentInfo}>
                Palvelumaksu: {payment.products[0].serviceFee}€
              </Typography>
            )}
            {payment.discountCode !== undefined && (
              <Fragment>
                <Typography className={classes.paymentInfo}>
                  Alennuskoodi: {payment.discountCode.name}
                </Typography>
                <Typography className={classes.paymentInfo}>
                  Alennus: {payment.discountCode.discount}%
                </Typography>
              </Fragment>
            )}
            <div aria-live="polite" aria-atomic="true">
              {selectedFeeCents > 0 && (
                <Typography className={classes.paymentInfo}>
                  {`Maksutapalisä (OP Lasku): +${euroFormatter.format(
                    selectedFeeCents / 100
                  )}`}
                </Typography>
              )}
              <Typography
                className={`${classes.paymentInfo} ${classes.totalPrice}`}
              >
                Hinta yhteensä:{' '}
                {totalCents === undefined
                  ? '—'
                  : euroFormatter.format(totalCents / 100)}
              </Typography>
            </div>
          </div>
        )}

        {isResumePayment && hasImmutablePaymentTotal && (
          <div id="payment-dialog-description" className={classes.priceSummary}>
            <Typography
              className={`${classes.paymentInfo} ${classes.totalPrice}`}
            >
              Hinta yhteensä:{' '}
              {euroFormatter.format(payment.paymentTotalCents / 100)}
            </Typography>
          </div>
        )}

        {paymentError && (
          <Alert severity="error" className={classes.errorAlert}>
            {paymentError}
          </Alert>
        )}

        {!isResumePayment && (
          <Typography className={`${classes.paymentInfo} ${classes.terms}`}>
            Valitsemalla maksutavan hyväksyt{' '}
            <a
              className={classes.termsLink}
              href="https://www.paytrail.com/kuluttaja/maksupalveluehdot"
              target="_blank"
              rel="noopener noreferrer"
            >
              maksupalveluehdot
            </a>
          </Typography>
        )}

        <section
          className={classes.providerSection}
          aria-labelledby="provider-heading"
        >
          <Typography
            id="provider-heading"
            component="h3"
            className={classes.providerHeading}
          >
            {isResumePayment ? 'Jatka maksua' : 'Valitse maksutapa'}
          </Typography>

          {isResumePayment ? (
            paymentError ? (
              <Button
                ref={resumeRetryButtonRef}
                className={classes.retryButton}
                variant="outlined"
                onClick={resumePayment}
              >
                Yritä uudelleen
              </Button>
            ) : null
          ) : providerError ? (
            <Fragment>
              <Alert severity="error">{providerError}</Alert>
              <Button
                className={classes.retryButton}
                variant="outlined"
                onClick={loadProviders}
              >
                Yritä uudelleen
              </Button>
            </Fragment>
          ) : !isLoadingProviders && availableProviders.length === 0 ? (
            <Alert severity="info">Maksutapoja ei ole saatavilla.</Alert>
          ) : availableProviders.length > 0 ? (
            <div
              className={classes.providerGrid}
              role="group"
              aria-label="Maksutavat"
              aria-busy={isCreatingPayment}
            >
              {availableProviders.map((provider) => {
                const isInvoice =
                  provider.paymentMethod === PAYMENT_METHODS.OP_INVOICE;
                const isSelected = selectedProvider?.id === provider.id;
                const providerFee = getProviderFee(provider);

                return (
                  <Button
                    key={`${provider.paymentMethod}-${provider.id}`}
                    ref={(node) => {
                      providerButtonRefs.current[provider.id] = node;
                    }}
                    className={`${classes.providerTile} ${
                      isSelected ? classes.selectedProviderTile : ''
                    }`}
                    variant="outlined"
                    disabled={isCreatingPayment}
                    aria-pressed={isSelected}
                    onClick={() => selectProvider(provider)}
                    data-provider-id={provider.id}
                  >
                    <span className={classes.providerTileContent}>
                      {provider.svg && (
                        <img
                          className={classes.providerLogo}
                          src={provider.svg}
                          alt=""
                          aria-hidden="true"
                        />
                      )}
                      <span className={classes.providerName}>
                        {provider.name}
                      </span>
                      {isInvoice && (
                        <span className={classes.invoiceBadge}>
                          {`Maksutapalisä (OP Lasku) +${euroFormatter.format(
                            providerFee
                          )}`}
                        </span>
                      )}
                    </span>
                  </Button>
                );
              })}
            </div>
          ) : null}

          {!isResumePayment && availableProviders.length > 0 && (
            <div className={classes.confirmSection}>
              <Button
                ref={confirmButtonRef}
                className={classes.confirmButton}
                variant="contained"
                disableElevation
                disabled={!selectedProvider || isCreatingPayment}
                onClick={() => createPayment(selectedProvider)}
              >
                {confirmLabel}
              </Button>
            </div>
          )}

          <div
            className={classes.paymentStatus}
            role="status"
            aria-live="polite"
          >
            {statusText && (
              <Fragment>
                <CircularProgress
                  className={classes.circularProgress}
                  size={22}
                  thickness={5}
                  aria-hidden="true"
                />
                <span>{statusText}</span>
              </Fragment>
            )}
          </div>
        </section>

        {paymentData &&
          responseToForms(paymentData, classes.hiddenForm, payment._id)}
      </DialogContent>
      <DialogActions>
        <IconButton
          onClick={handleDialogClose}
          className={classes.closeButton}
          aria-label={
            isResumePayment
              ? 'Sulje maksun jatkaminen'
              : 'Sulje maksutavan valinta'
          }
        >
          <Icon className="fas fa-times" aria-hidden="true" />
        </IconButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
